import React from "react";
import { Share2, Copy, Mail, MessageCircle, X } from "lucide-react";

/**
 * Multi-network share dialog for a product page.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string} props.url - canonical product URL (e.g. https://edicolavillaricca.com/prodotto/{slug})
 * @param {string} props.shareUrl - URL with server-rendered OG tags (e.g. https://.../api/og/prodotto/{slug})
 * @param {string} props.title
 * @param {string} props.description
 * @param {string} [props.image]
 * @param {string} [props.price] - already-formatted (e.g. "€ 26,99")
 * @param {(msg: string) => void} props.onToast
 */
export const ShareDialog = ({ open, onClose, url, shareUrl, title, description, image, price, onToast }) => {
  if (!open) return null;

  // Social bots only ever crawl the URL we send them, so we always send the
  // /api/og/... URL with server-rendered meta tags. Human clicks on that URL
  // are auto-redirected to the canonical product page (meta-refresh + JS).
  const target = shareUrl || url;
  const enc = encodeURIComponent;
  const shortMsg = price ? `${title} — ${price}` : title;
  const fullMsg = `${shortMsg}\n\n${description ? description.slice(0, 140) + (description.length > 140 ? "…" : "") : ""}`;

  // Pre-built share intents (each social network has a documented URL scheme)
  // Messenger requires a real Meta App ID (free, registered at developers.facebook.com).
  // We read it from REACT_APP_FB_APP_ID; without it, the desktop fallback won't open.
  const fbAppId = process.env.REACT_APP_FB_APP_ID || "";
  const networks = [
    {
      key: "whatsapp",
      label: "WhatsApp",
      color: "bg-[#25D366] hover:bg-[#22c55e]",
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
        </svg>
      ),
      href: `https://wa.me/?text=${enc(fullMsg + "\n\n" + target)}`,
    },
    {
      key: "facebook",
      label: "Facebook",
      color: "bg-[#1877F2] hover:bg-[#1668d8]",
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      ),
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(target)}&quote=${enc(shortMsg)}`,
    },
    {
      key: "messenger",
      label: "Messenger",
      color: "bg-[#0084FF] hover:bg-[#0070d8]",
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
          <path d="M12.001 0C5.388 0 0 4.97 0 11.118c0 3.466 1.71 6.547 4.382 8.578v4.304l4.013-2.207c1.072.295 2.21.456 3.605.456 6.612 0 12-4.97 12-11.13C24 4.97 18.613 0 12.001 0M13.2 14.957l-3.06-3.255-5.957 3.255 6.553-6.956 3.135 3.255 5.881-3.255-6.553 6.956Z" />
        </svg>
      ),
      href: `fb-messenger://share/?link=${enc(target)}&app_id=${fbAppId}`,
      // Desktop fallback to FB Send Dialog if Messenger app isn't installed
      fallback: fbAppId
        ? `https://www.facebook.com/dialog/send?app_id=${fbAppId}&link=${enc(target)}&redirect_uri=${enc(target)}`
        : `https://www.facebook.com/sharer/sharer.php?u=${enc(target)}`,
    },
    {
      key: "twitter",
      label: "X / Twitter",
      color: "bg-black hover:bg-gray-800",
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      ),
      href: `https://twitter.com/intent/tweet?text=${enc(shortMsg)}&url=${enc(target)}`,
    },
    {
      key: "linkedin",
      label: "LinkedIn",
      color: "bg-[#0A66C2] hover:bg-[#0958ab]",
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
      ),
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(target)}`,
    },
    {
      key: "telegram",
      label: "Telegram",
      color: "bg-[#0088CC] hover:bg-[#0078b3]",
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
          <path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
        </svg>
      ),
      href: `https://t.me/share/url?url=${enc(target)}&text=${enc(shortMsg)}`,
    },
    {
      key: "pinterest",
      label: "Pinterest",
      color: "bg-[#E60023] hover:bg-[#cc001f]",
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
          <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.624 0 11.99-5.367 11.99-11.987C24.007 5.367 18.641.001 12.017.001z" />
        </svg>
      ),
      href: `https://pinterest.com/pin/create/button/?url=${enc(target)}&media=${enc(image || "")}&description=${enc(shortMsg)}`,
    },
    {
      key: "email",
      label: "Email",
      color: "bg-gray-600 hover:bg-gray-700",
      icon: <Mail className="w-5 h-5" />,
      href: `mailto:?subject=${enc(shortMsg)}&body=${enc(fullMsg + "\n\n" + target)}`,
    },
    {
      key: "sms",
      label: "SMS",
      color: "bg-emerald-600 hover:bg-emerald-700",
      icon: <MessageCircle className="w-5 h-5" />,
      href: `sms:?&body=${enc(shortMsg + " " + target)}`,
    },
  ];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(target);
      onToast?.("📋 Link copiato! Incollalo dove vuoi.");
      onClose();
    } catch {
      onToast?.("Copia non riuscita");
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: shortMsg, text: description?.slice(0, 100), url: target });
        onClose();
      } catch {
        // User cancelled — keep dialog open
      }
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4"
      onClick={onClose}
      data-testid="share-dialog"
    >
      <div
        className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-gray-700" />
            <h3 className="font-semibold text-gray-900">Condividi prodotto</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
            aria-label="Chiudi"
            data-testid="share-dialog-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          {/* Native share (mobile only) */}
          {typeof navigator !== "undefined" && navigator.share && (
            <button
              onClick={handleNativeShare}
              className="w-full mb-3 px-4 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold flex items-center justify-center gap-2 hover:from-amber-600 hover:to-orange-600 transition"
              data-testid="share-native-btn"
            >
              <Share2 className="w-5 h-5" /> Condividi con un'app
            </button>
          )}

          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {networks.map((n) => (
              <a
                key={n.key}
                href={n.href}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex flex-col items-center gap-1 p-3 rounded-xl text-white font-medium text-xs transition ${n.color}`}
                onClick={(e) => {
                  // Messenger has a desktop fallback: fb-messenger:// only works on
                  // mobile (or when Messenger Desktop is installed). On regular
                  // desktop browsers the protocol click is a no-op, so we open
                  // the FB Send Dialog fallback in a new tab instead.
                  if (n.fallback) {
                    const isDesktop = !/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
                    if (isDesktop) {
                      e.preventDefault();
                      window.open(n.fallback, "_blank", "noopener,noreferrer");
                    }
                  }
                  // Close dialog after click — the new window opens already
                  setTimeout(onClose, 200);
                }}
                data-testid={`share-${n.key}`}
              >
                {n.icon}
                <span>{n.label}</span>
              </a>
            ))}
          </div>

          {/* Copy link section */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Oppure copia il link</label>
            <div className="flex gap-2">
              <input
                type="text"
                readOnly
                value={target}
                onClick={(e) => e.target.select()}
                className="flex-1 px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg font-mono truncate"
                data-testid="share-link-input"
              />
              <button
                onClick={handleCopy}
                className="px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-lg text-sm font-medium flex items-center gap-1"
                data-testid="share-copy-btn"
              >
                <Copy className="w-4 h-4" /> Copia
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
